/* eslint-disable no-param-reassign */
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'store';
import { createUrl, handleThunkError } from 'store/utils';
import {
  initialStateSlice,
  startLoading,
  loadingFailed,
  getPaginationReducers,
  StateSlice
} from 'store/slices';
import { availabilityDomain } from 'models';
import http from 'api/http';
import { DateTime } from 'luxon';
import { Availability } from 'models/Availability';
import { setSnackbar, SnackbarStatus } from 'store/slices/snackbar';
import { availabilityRequestEndDate } from 'views/Jobs/AddJobModal/InitialFormConfig';
import { includeTodayForAvailability } from 'auth/keycloak';

const availability = createSlice({
  name: availabilityDomain.plural,
  initialState: initialStateSlice,
  reducers: {
    ...startLoading<Availability>(),
    ...loadingFailed<Availability>(),
    ...getPaginationReducers<Availability>(),
    getAvailabilitySuccess(state, { payload }: PayloadAction<Availability[]>) {
      state.items = payload;
      state.page = initialStateSlice.page;
      state.isLoading = false;
      state.error = null;
    }
  }
});

export const availabilitySelector = createSelector(
  (state: RootState) => state,
  state =>
    state[availabilityDomain.plural as keyof RootState] as StateSlice<
      Availability
    >
);

export const {
  requestStart,
  requestFail,
  setPagination: setAvailabilityPagination,
  setTotalItems,
  setTotalPages,
  getAvailabilitySuccess
} = availability.actions;

export default availability.reducer;

export function fetchAvailability(postcode: string, jobType: string): AppThunk {
  return async dispatch => {
    try {
      dispatch(getAvailabilitySuccess([]));
      dispatch(requestStart());

      const startDate = DateTime.now()
        .plus({
          days: includeTodayForAvailability() ? 0 : 1
        })
        .toISODate();

      const { data } = await http.get<Availability[]>(
        createUrl(availabilityDomain),
        {
          params: {
            jobTypeId: jobType,
            postcode,
            startDate,
            endDate: availabilityRequestEndDate(),
            includeTomorrow: true,
            includeOutsideBookingWindow: true
          }
        }
      );

      if (data.length === 0) {
        dispatch(
          setSnackbar({
            title: 'Error',
            message: 'No appointments available.',
            show: true,
            status: SnackbarStatus.Error
          })
        );
      }

      dispatch(getAvailabilitySuccess(data));

      return null;
    } catch (error) {
      handleThunkError({
        error,
        requestFail,
        dispatch,
        domainType: availabilityDomain.type
      });

      return null;
    }
  };
}
